import { Action, Reducer } from 'redux';
import { AppThunkAction } from './';

export interface AppState {
    IsLoading?: boolean;
    Layouts?: Layout[];
    QueryStringParams?: any;    
    DataSources?: any[];
    RouteUrl?: string;
    MicroContentString?: MicroContentString;
    MicroContentLink?: MicroContentLink;
    MicroContentImage?: MicroContentImage;
    DataSet?: DataSet[];
    ContextData?: any[];
    EditMode?: string;
    CSSList?: CSS[];
    SiteConfiguration?: SiteConfiguration;
    ComponentsState?: KeyValue;
    RefreshPage?: boolean;
    MockData?: KeyValue,
    AuthorizedPages?:string[]
}

interface MicroContentString {    
    [key: string]: any
}

interface MicroContentLink {
    [key: string]: any
}

interface MicroContentImage {
    [key: string]: any
}

export interface DataSet {
    [key: string]: any
}

export interface Layout {
    Id: string;
    Zones: Zone[];
    DataSources: DataSource[];
}

export interface Zone {
    Id: string;
    Sections: Section[];
}

export interface Section {
    Id: string;
    Components: Component[];
    DataBindings: DataBind[];
    Properties: Property[];
    Zones: Zone[];
}

export interface Component {
    Id: string;    
    Type: string;
    DataBindings: DataBind[];
    Template: Template;
    Style: Style;
    Properties: Property[];
    DynamicScripts: DynamicScript[];
    Subscribers: Subscriber[]
}


export interface DataSource {
    Id: string;
}

export interface DataBind {
    Name: string;
    DataPath: string;
}

export interface Property {
    Name: string;
    Value: any;
}

export interface Template {
    Id: string,
    Elements?: Element[]
}

export interface Element {
    Name: string,
    IsRendered?: string;
    IsComponent?: boolean,
    ItemEdit?: boolean,
    Properties?: Attribute[],
    InnerElements?: Element[]
    Component?: Component;
    IsRepeated?: boolean;
    DataBindings: DataBind[];
}

export interface Attribute {
    Name: string,
    Value: string,
    IsDatabound: boolean,
    IsExpression: boolean,
    IsXml?: boolean,
    IsPlainText?: boolean,
    StyleProps?: StyleProps[],
    Events?: Event[]
}

export interface Style {
    Id: string
}

export interface CSS {
    Key?: string,
    Value?:string
}

export interface DynamicScript {
    Name: string;
    Script: string;
}

export interface KeyValue {
    [key: string]: any
}

export interface StyleProps {
    Key: string,
    Value: string,
    IsExpression: boolean,
    DataBindings: DataBind[];
}

export interface Event {
    Name: string,
    DynamicScript: string,
    SuccessHandlerScript:string,
    StateKey?: string,
    StateValue?: any[],
    IsPostback?: boolean
}

export interface Subscriber {
    Name: string,
    DynamicScript: string,
    StateKey?: string,
    StateValue?: any[],
    SuccessHandlerScript: string
}

export interface DynamicScript {
    Id: string,
    Url: string,
    Script: string
}


export interface SiteConfiguration {
    Components?: ESComponent[],
    DynamicScripts?: DynamicScript[],
    ExperienceStyle?: [],
    Layout?: string,
    LayoutStyle?: [],
    PageStyle?:[],
    DataSource?: [],
    ExperienceSecurityGroup?: string,
    IsAuthorized:boolean,
    AuthorizedPages?:string[]
}

export interface ESComponent {
    InstanceId?: string,
    ItemOrder?: number,
    TemplateContent?: string,
    TemplateContentNS?:string,
    MockData?: string;
    ComponentStyle?: ESComponentStyle[],
    Id?: string,
    CollectionId?: string,
    RelatedTemplates?: RelatedTemplates[]
}

export interface ESComponentStyle {
    Style?: string,
    Key?: string,
    Id?: string,
    CollectionId?: string
}

export interface RelatedTemplates {
    TemplateAssociated: AssociatedItem[],
    StyleAssociated?: AssociatedItem[]
}

export interface AssociatedItem {
    Key: string,
    Id?: string,
    CollectionId?: string
}

export interface GetLayoutAction { type: 'GET_LAYOUT' }
export interface SetLayoutAction { type: 'SET_LAYOUT', appState: AppState }
export interface SetDataSetAction { type: 'SET_DATASET', appState: AppState, dataSetResult: KeyValue, key: string }
export interface SetContextDataAction { type: 'SET_CONTEXTDATA', appState: AppState, contextData:any[] }
export interface SetCSSAction { type: 'SET_CSS', appState: AppState, css: CSS }
export interface SetComponentStateAction { type: 'SET_COMPONENTSTATE', appState: AppState, elementState: KeyValue, key: string }
export interface ClearComponentStateAction { type: 'CLEAR_COMPONENTSTATE', appState: AppState, key: string }

export type LayoutAction = GetLayoutAction | SetLayoutAction | SetDataSetAction | SetContextDataAction | SetCSSAction | SetComponentStateAction | ClearComponentStateAction;

export const layoutActionCreators = {
    getAppState: () => ({ type: 'GET_LAYOUT' } as GetLayoutAction),
    setAppState: (state: AppState) => ({ type: 'SET_LAYOUT', appState: state } as SetLayoutAction),
    setAppDataSet: (state: AppState, dataSet: KeyValue, key: string) => ({ type: 'SET_DATASET', appState: state, dataSetResult: dataSet, key } as SetDataSetAction),
    setContextData: (state: AppState, contextData: any[]) => ({ type: 'SET_CONTEXTDATA', appState: state, contextData} as SetContextDataAction),
    setCSS: (state: AppState, cssValue: CSS) => ({ type: 'SET_CSS', appState: state, css: cssValue } as SetCSSAction),
    setComponentState: (state: AppState, elementState: KeyValue, key: string) => ({ type: 'SET_COMPONENTSTATE', appState: state, elementState, key } as SetComponentStateAction),
    clearComponentState: (state: AppState, key: string) => ({ type: 'CLEAR_COMPONENTSTATE', appState: state, key } as ClearComponentStateAction)
};

export const reducer: Reducer<AppState> = (state: AppState | undefined, incomingAction: Action): AppState => {    
    if (state === undefined) {
        return {
            IsLoading: true,
            Layouts: [],
            QueryStringParams: {},
            AuthorizedPages:[]
        };
    }

    const action = incomingAction as LayoutAction;
    switch (action.type) {
        case 'GET_LAYOUT':
            return { ...state, IsLoading: false  };
        case 'SET_LAYOUT':
            return { ...state, IsLoading: false, ...action.appState };
        case 'SET_DATASET':
            let dataSet = state.DataSet ? [...state.DataSet] : [];
            let itemIndex = dataSet.findIndex(t => t[action.key] !== undefined);
            if (itemIndex > -1) {
                dataSet[itemIndex] = action.dataSetResult;
            }
            else {
                dataSet.push(action.dataSetResult);
            }            
            return { ...state, IsLoading: false, DataSet: dataSet };
        case 'SET_CONTEXTDATA':
            return { ...state, IsLoading: false, ContextData: action.contextData };
        case 'SET_CSS':
            let cssList = state.CSSList ? [...state.CSSList] : [];            
            cssList.push(action.css);
            return { ...state, IsLoading: false, CSSList: cssList };
        case 'SET_COMPONENTSTATE':
            let componentState = state.ComponentsState ? { ...state.ComponentsState } : {};            
            componentState[action.key] = { ...componentState[action.key], ...action.elementState };
            action.appState.ComponentsState = componentState;
            return {
                ...state, IsLoading: false, ComponentsState: { ...componentState }
            };
        case 'CLEAR_COMPONENTSTATE':
            componentState = state.ComponentsState ? { ...state.ComponentsState } : {};
            componentState[action.key] = {};
            action.appState.ComponentsState = componentState;
            return {
                ...state, IsLoading: false, ComponentsState: { ...componentState }
            };
        default:
            return state;
    }
};